/**
 * React Query hooks for the CRM section.
 *
 *   useCrmAccounts(filters)   GET /v1/crm/accounts          (cursor paginated)
 *   useCrmAccount(id)         GET /v1/crm/accounts/{id}     (detail + contacts + opps + orders)
 *   useCrmOpportunities(f)    GET /v1/crm/opportunities     (cursor paginated)
 *   useCrmOpportunity(id)     GET /v1/crm/opportunities/{id} (detail + activity trail)
 *   useCrmPipeline()          GET /v1/crm/pipeline          (stages + their opps)
 *   useCrmScorecard()         GET /v1/crm/scorecard         (rollups)
 *   useCrmStages()            GET /v1/crm/stages
 *   useCrmReps()              GET /v1/crm/reps
 *   useCreateOpportunity()    POST  /v1/crm/opportunities
 *   useUpdateOpportunity()    PATCH /v1/crm/opportunities/{id}
 *   useMoveStage()            POST  /v1/crm/opportunities/{id}/stage
 *   useAddOppNote()           POST  /v1/crm/opportunities/{id}/note
 *   useCloseOpportunity()     POST  /v1/crm/opportunities/{id}/close
 *   usePatchAccount()         PATCH /v1/crm/accounts/{id}
 *
 * The CRM list endpoints use keyset (cursor) pagination: the response carries a
 * `next_cursor` string, which we feed straight back as the `cursor` query param.
 */

import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiGet, apiPatch, apiPost } from './api';
import { API_BASE } from './apiBase';

export const CRM_API_BASE = `${API_BASE}/v1/crm`;

// -------- capability helpers -------------------------------------------------

// Binary access-grant model (spec 00_access-binary-grants.md, 2026-08-09):
// each CRM tool is its own accessSlug, no read/write levels — holding the
// tool grants all of its APIs. Replaces the old crm:read / crm:write pair,
// which never distinguished Accounts from Pipeline from the Scorecard.
export function hasCrmAccounts(perms: string[] | undefined, superAdmin = false): boolean {
  if (superAdmin) return true;
  return !!perms?.includes('crm.accounts');
}
export function hasCrmPipeline(perms: string[] | undefined, superAdmin = false): boolean {
  if (superAdmin) return true;
  return !!perms?.includes('crm.pipeline');
}
export function hasCrmScorecard(perms: string[] | undefined, superAdmin = false): boolean {
  if (superAdmin) return true;
  return !!perms?.includes('crm.scorecard');
}

// -------- shared types (mirror tools/crm/src/crm-shared.ts) -------------------

export type AccountTier = 'key' | 'growth' | 'watch' | 'standard';
export type AccountStatus = 'active' | 'inactive' | 'prospect';
export type OpportunityStatus = 'open' | 'won' | 'lost';

// Row from crm_account_rollup (account + realized-order and open-pipeline aggregates).
export type AccountRow = {
  id: number;
  sage_division_no: string;
  sage_customer_no: string;
  name: string;
  brand: string;
  terms: string | null;
  sage_salesperson_no: string | null;
  owner_rep_id: number | null;
  tier: AccountTier;
  status: AccountStatus;
  source: string;
  sage_synced_at: string | null;
  created_at: string | null;
  updated_at: string | null;
  order_count: number;
  open_order_amount: string | null;
  last_order_date: string | null;
  open_opp_count: number;
  weighted_pipeline: string | null;
};

export type ContactRow = {
  id: number;
  account_id: number;
  name: string;
  title: string | null;
  email: string | null;
  phone: string | null;
  is_primary: boolean;
  created_at: string | null;
};

export type RecentOrderRow = {
  order_id: string;
  source_channel: string;
  source_order_id: string;
  order_date: string;
  status: string;
  ship_status: string;
  total_amount: string | null;
  currency: string;
  sage_so_number: string | null;
  sage_invoice_no: string | null;
};

export type OpportunityRow = {
  id: number;
  opp_no: string;
  account_id: number;
  account_name: string | null;
  sales_rep_id: number | null;
  sales_rep_name: string | null;
  stage_id: number;
  stage_code: string | null;
  stage_name: string | null;
  title: string;
  transaction_type: string | null;
  projected_amount: string | null;
  probability: string | null;
  weighted_amount: string | null;
  expected_close_date: string | null;
  status: OpportunityStatus;
  close_date: string | null;
  memo: string | null;
  source: string;
  created_by: string | null;
  created_at: string | null;
  updated_at: string | null;
};

export type StageRow = {
  id: number;
  code: string;
  name: string;
  sort_order: number;
  default_probability: string | null;
  is_open: boolean;
  is_won: boolean;
  is_lost: boolean;
};

export type RepRow = {
  id: number;
  name: string;
  sage_salesperson_no: string | null;
  cockpit_user_id: string | null;
  email: string | null;
  active: boolean;
};

export type ActivityType =
  | 'created'
  | 'note'
  | 'stage_change'
  | 'field_change'
  | 'won'
  | 'lost'
  | 'reopened';

export type ActivityRow = {
  id: number;
  opportunity_id: number;
  type: ActivityType;
  actor: string | null;
  from_stage_id: number | null;
  from_stage_name: string | null;
  to_stage_id: number | null;
  to_stage_name: string | null;
  body: string | null;
  created_at: string | null;
};

// -------- accounts list ------------------------------------------------------

export type AccountFilters = {
  q?: string;
  tier?: string; // comma-separated tier values
  owner_rep_id?: number;
  status?: string; // comma-separated status values
};

type ListAccountsResponse = {
  accounts: AccountRow[];
  next_cursor: string | null;
  total_count: number | null;
  limit: number;
};

function buildAccountsQs(f: AccountFilters, cursor: string | null): string {
  const p = new URLSearchParams();
  if (f.q) p.set('q', f.q);
  if (f.tier) p.set('tier', f.tier);
  if (f.status) p.set('status', f.status);
  if (f.owner_rep_id !== undefined) p.set('owner_rep_id', String(f.owner_rep_id));
  p.set('limit', '50');
  if (cursor) p.set('cursor', cursor);
  return p.toString();
}

export function useCrmAccounts(filters: AccountFilters = {}) {
  const query = useInfiniteQuery<ListAccountsResponse, Error>({
    queryKey: ['crm-accounts', filters],
    queryFn: ({ pageParam }) =>
      apiGet<ListAccountsResponse>(
        `${CRM_API_BASE}/accounts?${buildAccountsQs(filters, (pageParam as string | null) ?? null)}`,
      ),
    initialPageParam: null,
    getNextPageParam: (lastPage) => lastPage.next_cursor ?? undefined,
    staleTime: 15_000,
    retry: false,
  });
  const rows: AccountRow[] = (query.data?.pages ?? []).flatMap((p) => p.accounts);
  return {
    rows,
    totalCount: query.data?.pages[0]?.total_count ?? null,
    isLoading: query.isLoading,
    isFetchingNextPage: query.isFetchingNextPage,
    hasNextPage: query.hasNextPage ?? false,
    fetchNextPage: () => query.fetchNextPage(),
    error: query.error,
  };
}

// -------- account detail -----------------------------------------------------

export type AccountDetailResponse = {
  account: AccountRow;
  contacts: ContactRow[];
  open_opportunities: OpportunityRow[];
  recent_orders: RecentOrderRow[];
};

export function useCrmAccount(id: string | number | null | undefined) {
  return useQuery<AccountDetailResponse, Error>({
    queryKey: ['crm-account', String(id ?? '')],
    queryFn: () => apiGet<AccountDetailResponse>(`${CRM_API_BASE}/accounts/${encodeURIComponent(String(id ?? ''))}`),
    enabled: id !== null && id !== undefined && String(id) !== '',
    retry: false,
  });
}

// -------- opportunities list -------------------------------------------------

export type OpportunityFilters = {
  stage?: string; // comma-separated stage_id values
  rep?: number; // sales_rep_id
  account?: number; // account_id
  status?: string; // comma-separated open|won|lost
};

type ListOpportunitiesResponse = {
  opportunities: OpportunityRow[];
  next_cursor: string | null;
  total_count: number | null;
  limit: number;
};

function buildOppsQs(f: OpportunityFilters, cursor: string | null): string {
  const p = new URLSearchParams();
  if (f.stage) p.set('stage', f.stage);
  if (f.status) p.set('status', f.status);
  if (f.rep !== undefined) p.set('rep', String(f.rep));
  if (f.account !== undefined) p.set('account', String(f.account));
  p.set('limit', '50');
  if (cursor) p.set('cursor', cursor);
  return p.toString();
}

export function useCrmOpportunities(filters: OpportunityFilters = {}) {
  const query = useInfiniteQuery<ListOpportunitiesResponse, Error>({
    queryKey: ['crm-opportunities', filters],
    queryFn: ({ pageParam }) =>
      apiGet<ListOpportunitiesResponse>(
        `${CRM_API_BASE}/opportunities?${buildOppsQs(filters, (pageParam as string | null) ?? null)}`,
      ),
    initialPageParam: null,
    getNextPageParam: (lastPage) => lastPage.next_cursor ?? undefined,
    staleTime: 15_000,
    retry: false,
  });
  const rows: OpportunityRow[] = (query.data?.pages ?? []).flatMap((p) => p.opportunities);
  return {
    rows,
    totalCount: query.data?.pages[0]?.total_count ?? null,
    isLoading: query.isLoading,
    isFetchingNextPage: query.isFetchingNextPage,
    hasNextPage: query.hasNextPage ?? false,
    fetchNextPage: () => query.fetchNextPage(),
    error: query.error,
  };
}

// -------- opportunity detail (+ activity trail) ------------------------------

export type OpportunityDetailResponse = {
  opportunity: OpportunityRow;
  activity: ActivityRow[];
};

export function useCrmOpportunity(id: string | number | null | undefined) {
  return useQuery<OpportunityDetailResponse, Error>({
    queryKey: ['crm-opportunity', String(id ?? '')],
    queryFn: () =>
      apiGet<OpportunityDetailResponse>(`${CRM_API_BASE}/opportunities/${encodeURIComponent(String(id ?? ''))}`),
    enabled: id !== null && id !== undefined && String(id) !== '',
    retry: false,
  });
}

// -------- pipeline board -----------------------------------------------------

export type PipelineStage = StageRow & {
  opportunities: OpportunityRow[];
  count: number;
  weighted_amount: string;
};

export type PipelineResponse = { stages: PipelineStage[] };

export function useCrmPipeline() {
  return useQuery<PipelineResponse, Error>({
    queryKey: ['crm-pipeline'],
    queryFn: () => apiGet<PipelineResponse>(`${CRM_API_BASE}/pipeline`),
    staleTime: 15_000,
    retry: false,
  });
}

// -------- scorecard ----------------------------------------------------------

export type ScorecardByMonth = {
  month: string;
  opp_count: number;
  weighted_amount: string | null;
  projected_amount: string | null;
};

export type ScorecardByRep = {
  sales_rep_id: number | null;
  sales_rep_name: string | null;
  open_opp_count: number;
  weighted_amount: string | null;
  projected_amount: string | null;
};

export type ScorecardAttention = OpportunityRow & { reason: string };

export type ScorecardResponse = {
  fiscal_quarter: { start: string; end_exclusive: string };
  weighted_pipeline_by_month: ScorecardByMonth[];
  won_this_quarter: { won_count: number; won_amount: string | null };
  weighted_pipeline_by_rep: ScorecardByRep[];
  needs_attention: ScorecardAttention[];
};

export function useCrmScorecard() {
  return useQuery<ScorecardResponse, Error>({
    queryKey: ['crm-scorecard'],
    queryFn: () => apiGet<ScorecardResponse>(`${CRM_API_BASE}/scorecard`),
    staleTime: 30_000,
    retry: false,
  });
}

// -------- stages + reps lookups ----------------------------------------------

export function useCrmStages() {
  const query = useQuery<{ stages: StageRow[] }, Error>({
    queryKey: ['crm-stages'],
    queryFn: () => apiGet<{ stages: StageRow[] }>(`${CRM_API_BASE}/stages`),
    staleTime: 5 * 60_000,
    retry: false,
  });
  return { stages: query.data?.stages ?? [], isLoading: query.isLoading, error: query.error };
}

export function useCrmReps() {
  const query = useQuery<{ reps: RepRow[] }, Error>({
    queryKey: ['crm-reps'],
    queryFn: () => apiGet<{ reps: RepRow[] }>(`${CRM_API_BASE}/reps`),
    staleTime: 5 * 60_000,
    retry: false,
  });
  return { reps: query.data?.reps ?? [], isLoading: query.isLoading, error: query.error };
}

// -------- writes -------------------------------------------------------------

export type CreateOpportunityBody = {
  account_id: number;
  stage_id: number;
  title: string;
  sales_rep_id?: number | null;
  transaction_type?: string | null;
  projected_amount?: number;
  probability?: number;
  expected_close_date?: string | null;
  memo?: string | null;
};

// Invalidate every query that reflects opportunity state after a write.
function invalidateOppQueries(qc: ReturnType<typeof useQueryClient>, oppId?: number) {
  void qc.invalidateQueries({ queryKey: ['crm-opportunities'] });
  void qc.invalidateQueries({ queryKey: ['crm-pipeline'] });
  void qc.invalidateQueries({ queryKey: ['crm-scorecard'] });
  void qc.invalidateQueries({ queryKey: ['crm-accounts'] });
  void qc.invalidateQueries({ queryKey: ['crm-account'] });
  if (oppId !== undefined) void qc.invalidateQueries({ queryKey: ['crm-opportunity', String(oppId)] });
}

export function useCreateOpportunity() {
  const qc = useQueryClient();
  return useMutation<{ opportunity_id: number; opp_no?: string }, Error, CreateOpportunityBody>({
    mutationFn: (body) => apiPost(`${CRM_API_BASE}/opportunities`, body),
    onSuccess: () => invalidateOppQueries(qc),
  });
}

export type UpdateOpportunityBody = {
  title?: string;
  projected_amount?: number;
  probability?: number;
  expected_close_date?: string | null;
  sales_rep_id?: number | null;
  memo?: string | null;
};

export function useUpdateOpportunity() {
  const qc = useQueryClient();
  return useMutation<{ opportunity_id: number; updated: boolean }, Error, { id: number; body: UpdateOpportunityBody }>({
    mutationFn: ({ id, body }) => apiPatch(`${CRM_API_BASE}/opportunities/${id}`, body),
    onSuccess: (_d, { id }) => invalidateOppQueries(qc, id),
  });
}

export function useMoveStage() {
  const qc = useQueryClient();
  return useMutation<
    { opportunity_id: number; stage_id: number; probability: string },
    Error,
    { id: number; stage_id: number; probability?: number }
  >({
    mutationFn: ({ id, stage_id, probability }) =>
      apiPost(`${CRM_API_BASE}/opportunities/${id}/stage`, { stage_id, probability }),
    onSuccess: (_d, { id }) => invalidateOppQueries(qc, id),
  });
}

export function useAddOppNote() {
  const qc = useQueryClient();
  return useMutation<{ opportunity_id: number; noted: boolean }, Error, { id: number; body: string }>({
    mutationFn: ({ id, body }) => apiPost(`${CRM_API_BASE}/opportunities/${id}/note`, { body }),
    onSuccess: (_d, { id }) => invalidateOppQueries(qc, id),
  });
}

export function useCloseOpportunity() {
  const qc = useQueryClient();
  return useMutation<
    { opportunity_id: number; status: string; close_date: string },
    Error,
    { id: number; result: 'won' | 'lost'; close_date: string }
  >({
    mutationFn: ({ id, result, close_date }) =>
      apiPost(`${CRM_API_BASE}/opportunities/${id}/close`, { result, close_date }),
    onSuccess: (_d, { id }) => invalidateOppQueries(qc, id),
  });
}

export type PatchAccountBody = {
  owner_rep_id?: number | null;
  tier?: AccountTier;
  status?: AccountStatus;
};

export function usePatchAccount() {
  const qc = useQueryClient();
  return useMutation<{ account_id: number; updated: boolean }, Error, { id: number; body: PatchAccountBody }>({
    mutationFn: ({ id, body }) => apiPatch(`${CRM_API_BASE}/accounts/${id}`, body),
    onSuccess: (_d, { id }) => {
      void qc.invalidateQueries({ queryKey: ['crm-accounts'] });
      void qc.invalidateQueries({ queryKey: ['crm-account', String(id)] });
    },
  });
}

// -------- formatting helpers -------------------------------------------------

// Format a decimal-string dollar amount (the API returns DECIMAL as a string).
export function formatCrmMoney(v: string | number | null | undefined): string {
  if (v == null) return '--';
  const n = typeof v === 'number' ? v : Number(v);
  if (!Number.isFinite(n)) return '--';
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(n);
}

// Format a 0..1 probability decimal string as a whole-number percent.
export function formatProbabilityPct(v: string | number | null | undefined): string {
  if (v == null) return '--';
  const n = typeof v === 'number' ? v : Number(v);
  if (!Number.isFinite(n)) return '--';
  return `${Math.round(n * 100)}%`;
}

const TIER_LABELS: Record<AccountTier, string> = {
  key: 'Key',
  growth: 'Growth',
  watch: 'Watch',
  standard: 'Standard',
};
export function tierLabel(t: string): string {
  return TIER_LABELS[t as AccountTier] ?? t;
}

// Pill tones for account tier. Key reads as the strongest accent.
export const TIER_TONES: Record<AccountTier, string> = {
  key: 'bg-amp-olive-deep text-amp-white dark:bg-amp-olive-dark dark:text-amp-dark-bg',
  growth: 'bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300',
  watch: 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300',
  standard: 'bg-gray-100 text-gray-600 dark:bg-gray-800/60 dark:text-gray-300',
};
export function tierTone(t: string): string {
  return TIER_TONES[t as AccountTier] ?? TIER_TONES.standard;
}

const STATUS_LABELS: Record<AccountStatus, string> = {
  active: 'Active',
  inactive: 'Inactive',
  prospect: 'Prospect',
};
export function accountStatusLabel(s: string): string {
  return STATUS_LABELS[s as AccountStatus] ?? s;
}

export const ACCOUNT_STATUS_TONES: Record<AccountStatus, string> = {
  active: 'bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300',
  prospect: 'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300',
  inactive: 'bg-gray-100 text-gray-600 dark:bg-gray-800/60 dark:text-gray-300',
};
export function accountStatusTone(s: string): string {
  return ACCOUNT_STATUS_TONES[s as AccountStatus] ?? ACCOUNT_STATUS_TONES.inactive;
}

export const OPP_STATUS_TONES: Record<OpportunityStatus, string> = {
  open: 'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300',
  won: 'bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300',
  lost: 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300',
};
export function oppStatusTone(s: string): string {
  return OPP_STATUS_TONES[s as OpportunityStatus] ?? OPP_STATUS_TONES.open;
}

export const ACCOUNT_TIER_OPTIONS: { value: AccountTier; label: string }[] = [
  { value: 'key', label: 'Key' },
  { value: 'growth', label: 'Growth' },
  { value: 'watch', label: 'Watch' },
  { value: 'standard', label: 'Standard' },
];

export const ACCOUNT_STATUS_OPTIONS: { value: AccountStatus; label: string }[] = [
  { value: 'active', label: 'Active' },
  { value: 'inactive', label: 'Inactive' },
  { value: 'prospect', label: 'Prospect' },
];
